/**
 * PublicPageFooter — THE shared footer for all public/content pages.
 * Used by: /, /feed, /travel-brief, /briefs, /podcast, /newsletters,
 * /newsletter/:id, /reports, /subscribe, /about, /developers.
 * Supports light + dark themes via the `dark` prop.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Rss, Mail } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { API } from "../config";
import EmailCapture from "./EmailCapture";
import SuggestSourceModal from "./SuggestSourceModal";

const CONTENT_LINKS = [
  { label: "Travel Brief",  to: "/travel-brief" },
  { label: "Brief Archive", to: "/briefs" },
  { label: "Podcast",       to: "/podcast" },
  { label: "Newsletters",   to: "/newsletters" },
  { label: "Reports & Research", to: "/reports" },
  { label: "Officina Turistica (EN)", to: "/officina-turistica" },
  { label: "Industry Events", to: "/events" },
  { label: "AI Travel Statistics", to: "/ai-travel-statistics" },
  { label: "Industry Glossary", to: "/glossary" },
];

const COMPANY_LINKS = [
  { label: "About",     to: "/about" },
  { label: "Subscribe", to: "/subscribe" },
  { label: "Support",        to: "/support", staticHtml: true },
  { label: "Privacy Policy", to: "/privacy", staticHtml: true },
  { label: "Terms of Use",   to: "/terms",   staticHtml: true },
  { label: "Unsubscribe",    to: "/unsubscribe" },
];

const RSS_FEEDS = [
  { label: "All News",     href: `${API}/rss` },
  { label: "AI & Travel",  href: `${API}/rss/ai-travel` },
  { label: "Hotel Tech",   href: `${API}/rss/hotel-tech` },
  { label: "Podcast",      href: `${API}/rss/podcast` },
  { label: "Travel Briefs", href: `${API}/rss/briefs` },
  { label: "Newsletters",  href: `${API}/rss/newsletters` },
];

export default function PublicPageFooter({ dark: darkProp }) {
  const [pref] = useDarkMode();
  const dark = darkProp ?? pref;
  const { user } = useAuth() || {};
  const year = new Date().getFullYear();

  const heading = `text-[11px] font-bold uppercase tracking-widest mb-3 ${dark ? "text-slate-400" : "text-[#3b494c]"}`;
  const link = `text-xs transition-colors ${dark ? "text-slate-500 hover:text-cyan-400" : "text-[#6b7a7d] hover:text-[#006874]"}`;
  const faint = dark ? "text-slate-600" : "text-[#bac9cc]";

  return (
    <footer
      data-testid="public-page-footer"
      className={`border-t mt-auto ${dark ? "bg-[#0c1011] border-white/5" : "bg-[#f8f9fa] border-black/5"}`}
    >
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        {/* Newsletter signup — anonymous visitors only (subscription = platform access) */}
        {!user && (
          <div
            data-testid="footer-newsletter-box"
            className={`mb-10 pb-10 border-b flex flex-col md:flex-row md:items-start gap-6 ${dark ? "border-white/5" : "border-black/5"}`}
          >
            <div className="md:w-2/5">
              <h3 className={`editorial-heading text-xl font-semibold mb-1.5 ${dark ? "text-slate-100" : "text-[#191c1d]"}`}>
                The travel industry, decoded daily
              </h3>
              <p className={`text-xs leading-relaxed ${dark ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                Join the TourismIntel newsletter — AI-curated briefs, podcasts and research for DMOs and hoteliers. Free, unsubscribe anytime.
              </p>
            </div>
            <div className="md:flex-1 md:max-w-xl">
              <EmailCapture darkMode={dark} source="footer" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-12 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <Link to="/" data-testid="footer-logo-link" className="inline-block">
              <img
                src={dark ? "/TI_logo_white_crop.webp" : "/TI_logo_color_crop.webp"}
                alt="TourismIntel"
                width={120}
                height={26}
                style={{ height: 26, width: 120, objectFit: "contain", maxWidth: "none" }}
              />
            </Link>
            <p className={`text-xs mt-3 leading-relaxed max-w-[240px] ${dark ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              Tourism intelligence, not tourism noise.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a
                href="mailto:mirko@bereadyfor.ai"
                data-testid="footer-contact-email"
                className={`inline-flex items-center gap-1.5 ${link}`}
              >
                <Mail className="w-3.5 h-3.5" /> Contact
              </a>
              <a
                href="https://mirkolalli.me"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-mirko-credit"
                className={link}
              >
                by Mirko Lalli
              </a>
            </div>

            {/* Mobile app badges — App Store live, Google Play coming soon. */}
            <div className="mt-5">
              <h4 className={heading} style={{ marginBottom: 10 }}>
                Get the app
              </h4>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href="https://apps.apple.com/it/app/tourismintel/id6778354345"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-app-store-badge"
                  aria-label="Download TourismIntel on the App Store"
                  className="inline-block transition-opacity hover:opacity-80"
                >
                  <img
                    src="/badges/app-store-badge.svg"
                    alt="Download on the App Store"
                    height="50"
                    style={{ height: 50, width: "auto", display: "block" }}
                  />
                </a>
                <span
                  data-testid="footer-google-play-badge"
                  aria-label="TourismIntel on Google Play — coming soon"
                  title="Coming soon"
                  className="inline-block relative cursor-not-allowed"
                  style={{ opacity: 0.55 }}
                >
                  <img
                    src="/badges/google-play-badge.svg"
                    alt="Get it on Google Play (coming soon)"
                    height="50"
                    style={{ height: 50, width: "auto", display: "block" }}
                  />
                  <span
                    className={`absolute top-1 right-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      dark ? "bg-amber-500/30 text-amber-200" : "bg-amber-500/90 text-white"
                    }`}
                    style={{ letterSpacing: "0.05em" }}
                  >
                    Soon
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-3">
            <h4 className={heading}>Content</h4>
            <ul className="space-y-2">
              {CONTENT_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-link-${to.replace(/\//g, "")}`} className={link}>
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                {user ? (
                  <SuggestSourceModal darkMode={dark} trigger={
                    <button type="button" data-testid="footer-suggest-source-btn" className={`${link} text-left`}>
                      Suggest a source
                    </button>
                  } />
                ) : (
                  <Link to="/auth" data-testid="footer-suggest-source-btn" title="Sign in to suggest a source" className={link}>
                    Suggest a source
                  </Link>
                )}
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-2">
            <h4 className={heading}>Company</h4>
            <ul className="space-y-2">
              {COMPANY_LINKS.map(({ label, to, staticHtml }) => (
                <li key={to}>
                  {staticHtml ? (
                    // Use a real <a> for legal/support pages — forces a full HTTP
                    // request so the static SSR file at /public/{to}/index.html
                    // is served, instead of a React Router client-side navigation
                    // (which would render the SPA shell, no SEO / no Apple-review).
                    <a href={to} data-testid={`footer-link-${to.replace(/\//g, "")}`} className={link}>
                      {label}
                    </a>
                  ) : (
                    <Link to={to} data-testid={`footer-link-${to.replace(/\//g, "")}`} className={link}>
                      {label}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <Link to="/developers" data-testid="footer-link-developers" className={link}>
                  API for Developers
                </Link>
              </li>
              <li>
                <Link to="/mcp" data-testid="footer-link-mcp" className={`${link} inline-flex items-center gap-1.5`}>
                  MCP Server
                  <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${dark ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>
                    new!
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* RSS */}
          <div className="md:col-span-3">
            <h4 className={`${heading} flex items-center gap-1.5`}>
              <Rss className="w-3 h-3 text-orange-500" /> RSS Feeds
            </h4>
            <ul className="space-y-2">
              {RSS_FEEDS.map(({ label, href }) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-rss-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={link}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`mt-10 pt-5 border-t flex flex-col sm:flex-row items-center justify-between gap-2 ${dark ? "border-white/5" : "border-black/5"}`}>
          <p className={`text-xs ${faint}`} data-testid="footer-copyright">
            © {year} TourismIntel · All rights reserved
          </p>
          <p className={`text-xs ${faint}`}>
            Powered by AI · Built for the future of travel
          </p>
        </div>
      </div>
    </footer>
  );
}
